import { ErrorMessage } from '@hookform/error-message';
import React, { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ErrorMessageText from '../ErrorMessageText';
import HintText from '../HintText';
import Label from '../Label';
import EyeIcon from 'images/icons/Eye.svg?react';
import { SFormGroup, SPasswordWrapper, SToggleButton } from './styles';

export interface PasswordInputProps {
    name: string;
    label?: string;
    validate?(value: string): boolean | string;
    disabled?: boolean;
    hint?: string;
    required?: boolean;
    placeholder?: string;
    errorMessage?: string;
    pattern?: { value: RegExp; message: string };
    semi?: boolean;
    helper?: string;
    transform?: (value: string) => string;
}

const PasswordInput: FC<PasswordInputProps> = ({
    name,
    label,
    hint,
    disabled = false,
    placeholder,
    required = false,
    pattern,
    validate,
    helper,
    semi = false,
    transform,
    ...rest
}) => {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);
    const {
        formState: { errors },
        register,
    } = useFormContext();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        if (transform) {
            const transformedValue = transform(value);
            e.target.value = transformedValue;
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <SFormGroup semi={semi} withError={!!errors[name]}>
            {label && <Label helper={helper} name={name} required={required} text={label} />}
            <SPasswordWrapper withError={!!errors[name]}>
                <input
                    {...register(name, {
                        pattern,
                        required: required ? `${t('common.required-field')}` : false,
                        validate,
                    })}
                    disabled={disabled}
                    {...(placeholder && { placeholder: t(placeholder) })}
                    onChange={handleChange}
                    type={showPassword ? 'text' : 'password'}
                    {...rest}
                />
                <SToggleButton
                    aria-label={showPassword ? t('common.password.hide') : t('common.password.show')}
                    onClick={togglePasswordVisibility}
                    type="button"
                >
                    <EyeIcon />
                </SToggleButton>
            </SPasswordWrapper>
            {hint && <HintText text={hint} />}
            {errors[name] && (
                <ErrorMessage
                    errors={errors}
                    name={name}
                    render={({ message }) => <ErrorMessageText text={message} />}
                />
            )}
        </SFormGroup>
    );
};

export default PasswordInput;
